import { useContext, useEffect } from 'react'

import { DevicesContext } from '../../../contexts/devices_context'
import { usePersistedState } from '../../../hooks/use_persisted_state'
import {
  SELECTED_AUDIO_DEVICE,
  SELECTED_AUDIO_OUTPUT_DEVICE,
  SELECTED_VIDEO_DEVICE,
} from './use_local_tracks'

export const useManageSelectedDevices = () => {
  const {
    initialized,
    audioDevices,
    videoDevices,
    audioOutputDevices,
    hasAudioDevices,
    hasVideoDevices,
    hasAudioOutputDevices,
  } = useContext(DevicesContext)
  const [audioDeviceId, setAudioDeviceId] = usePersistedState<null | string>(
    SELECTED_AUDIO_DEVICE,
    null,
  )
  const [videoDeviceId, setVideoDeviceId] = usePersistedState<null | string>(
    SELECTED_VIDEO_DEVICE,
    null,
  )
  const [audioOutputDeviceId, setAudioOutputDeviceId] = usePersistedState<
    null | string
  >(SELECTED_AUDIO_OUTPUT_DEVICE, null)

  useEffect(() => {
    if (initialized) {
      const devices = Object.values(audioDevices)
      const selectedDeviceDisconnected =
        audioDeviceId &&
        !devices.find((device) => device.deviceId === audioDeviceId)
      if (!audioDeviceId || selectedDeviceDisconnected) {
        setAudioDeviceId(null)
      }
    }
  }, [audioDeviceId, audioDevices, initialized, setAudioDeviceId])

  useEffect(() => {
    if (initialized) {
      const devices = Object.values(videoDevices)
      const selectedDeviceDisconnected =
        videoDeviceId &&
        !devices.find((device) => device.deviceId === videoDeviceId)
      if (!videoDeviceId || selectedDeviceDisconnected) {
        setVideoDeviceId(null)
      }
    }
  }, [initialized, setVideoDeviceId, videoDeviceId, videoDevices])

  useEffect(() => {
    if (initialized) {
      const devices = Object.values(audioOutputDevices)
      const selectedDeviceDisconnected =
        audioOutputDeviceId &&
        !devices.find((device) => device.deviceId === audioOutputDeviceId)
      if (!audioOutputDeviceId || selectedDeviceDisconnected) {
        setAudioOutputDeviceId(null)
      }
    }
  }, [
    audioOutputDeviceId,
    audioOutputDevices,
    initialized,
    setAudioOutputDeviceId,
  ])

  return {
    audioDevices,
    videoDevices,
    audioOutputDevices,
    hasAudioDevices,
    hasVideoDevices,
    hasAudioOutputDevices,
    audioDeviceId,
    videoDeviceId,
    audioOutputDeviceId,
    setAudioDeviceId,
    setVideoDeviceId,
    setAudioOutputDeviceId,
  }
}
